/** Notification toasts (docs/08 §8.13). */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { uuid } from "@/lib/utils";

export interface Toast {
  id: string;
  message: string;
  tone: "success" | "error" | "info";
  /** An optional action, e.g. "Undo" or "Review". */
  action?: { label: string; onClick: () => void };
}

interface ToastValue {
  toasts: Toast[];
  notify: (message: string, tone?: Toast["tone"], action?: Toast["action"]) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

/** Errors stay until dismissed; everything else clears itself. */
const AUTO_DISMISS_MS = 4_000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, tone: Toast["tone"] = "info", action?: Toast["action"]) => {
      const toast: Toast = { id: uuid(), message, tone, action };
      setToasts((current) => [...current, toast]);

      if (tone !== "error") {
        setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS);
      }
    },
    [dismiss],
  );

  const value = useMemo(() => ({ toasts, notify, dismiss }), [toasts, notify, dismiss]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside a ToastProvider");
  return context;
}
